import { cookieConsentService } from '../cookieConsent';
import { sanitizeBrowserAnalyticsProperties } from './BrowserAnalyticsPayloadSanitizer';
import { LinkedInInsightAnalyticsProvider } from './LinkedInInsightAnalyticsProvider';
import {
  PosthogAnalyticsProvider,
  type AnalyticsCollectionMode,
} from './PosthogAnalyticsProvider';
import type { AnalyticsProvider, AnalyticsUser, AnalyticsUserProperties } from './AnalyticsProvider';
import type { ExplanationDetailCode } from '../../types/adrProfile';
import type { BillingPriceKey } from '../../types/billing';

export interface QuotaIncreaseRequestProperties {
  tokenResetAt?: string | null;
  tokenLimit?: number;
  tokensSpent?: number;
  remainingTokens?: number;
  source?: string;
}

export interface LoginPageViewProperties {
  page?: 'login' | 'register';
  source?: string;
  previousPathname?: string;
  trigger?: string;
}

export interface AuthSubmitAnalyticsProperties {
  email?: string;
  page?: 'login' | 'register';
  botProtectionEnabled?: boolean;
  botProtectionVerified?: boolean;
}

export interface DemoRequestClickAnalyticsProperties {
  source?: string;
}

export type TrustpilotReviewSurface = 'prompt_modal' | 'profile_sidebar';

export interface TrustpilotPromptAnalyticsProperties {
  completionType: 'question' | 'sds_analysis' | 'existing_history';
  completedUses: number;
}

type CookieConsentAnalyticsOutcome =
  | 'total_acceptance'
  | 'partial_acceptance'
  | 'total_refusal';

export interface CookieConsentAnalyticsProperties {
  audienceMeasurement: boolean;
  communicationTracking: boolean;
}

export interface AdrQuizAnswerAnalyticsProperties {
  questionId: string;
  category: string;
  difficulty: string;
}

export interface AdrQuizQuestionAnsweredAnalyticsProperties extends AdrQuizAnswerAnalyticsProperties {
  isCorrect: boolean;
}

export interface AdrProfileAnalyticsProperties {
  source: 'onboarding' | 'settings';
  outcome: 'completed' | 'skipped';
  adrLevelCode: string | null;
  adrRoleCodes: string[];
  countryCode: string | null;
  explanationDetailCode: ExplanationDetailCode | null;
}

export type BillingFunnelStep =
  | 'offer_viewed'
  | 'plan_selected'
  | 'registration_submitted'
  | 'company_country_viewed'
  | 'company_identifier_viewed'
  | 'company_confirmed';

export interface BillingFunnelAnalyticsProperties {
  step: BillingFunnelStep;
  source: string;
  priceKey?: BillingPriceKey;
  funnelType?: 'initial_subscription' | 'reactivation' | 'quota_pack';
  email?: string;
}

const BILLING_FUNNEL_STEP_ORDER: Record<BillingFunnelStep, number> = {
  offer_viewed: 1,
  plan_selected: 2,
  registration_submitted: 3,
  company_country_viewed: 4,
  company_identifier_viewed: 5,
  company_confirmed: 6,
};

class AnalyticsService {
  private readonly posthogProvider = new PosthogAnalyticsProvider();
  private readonly linkedInProvider = new LinkedInInsightAnalyticsProvider();
  private readonly providers: AnalyticsProvider[] = [
    this.posthogProvider,
    this.linkedInProvider,
  ];
  private lastPageViewKey: string | null = null;

  init(): void {
    this.posthogProvider.init(this.collectionMode());

    if (cookieConsentService.canUseCommunicationTracking()) {
      this.linkedInProvider.init();
    }
  }

  identifyUser(user: AnalyticsUser | null): void {
    if (!this.canUseStandardAnalytics()) {
      return;
    }

    this.posthogProvider.identifyUser(user);
    this.linkedInProvider.identifyUser(user);
  }

  reset(): void {
    this.posthogProvider.reset();
    this.linkedInProvider.reset();
    this.lastPageViewKey = null;
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    if (!this.canUseStandardAnalytics()) {
      return;
    }

    this.providers.forEach((provider) => provider.setUserProperties(properties));
  }

  capturePageView(properties: Record<string, unknown> = {}): void {
    if (typeof window === 'undefined') {
      return;
    }

    const mode = this.collectionMode();
    const isAnonymized = mode === 'pseudonymous';
    const distinctId = mode === 'standard'
      ? this.posthogProvider.standardDistinctId()
      : this.posthogProvider.pseudonymousDistinctId();
    const pageViewKey = `${window.location.href}:${distinctId}`;
    if (this.lastPageViewKey === pageViewKey) {
      return;
    }

    this.lastPageViewKey = pageViewKey;
    this.posthogProvider.capture(
      '$pageview',
      sanitizeBrowserAnalyticsProperties({
        ...this.getPageProperties(true),
        ...this.getPublicSurfaceProperties(),
        ...properties,
      }, isAnonymized),
      mode,
    );
  }

  captureLoginPageView(properties: LoginPageViewProperties = {}): void {
    const page = properties.page || 'login';
    this.capturePageView({
      page,
      source: properties.source || `${page}_route`,
      previous_pathname: properties.previousPathname,
      route_trigger: properties.trigger || 'route_navigation',
    });
  }

  async captureTryForFreeClick(): Promise<void> {
    await this.captureBrowserEvent('auth.try_for_free.clicked', {
      source: 'public_landing_cta',
    });
  }

  async captureDemoRequestClick(properties: DemoRequestClickAnalyticsProperties = {}): Promise<void> {
    await this.captureBrowserEvent('demo.request.clicked', {
      source: properties.source || 'public_landing_demo_cta',
    });
  }

  async captureTrustpilotPromptViewed(properties: TrustpilotPromptAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('trustpilot.prompt.viewed', {
      source: 'completion_threshold',
      completion_type: properties.completionType,
      completed_uses: properties.completedUses,
    });
  }

  async captureTrustpilotPromptDismissed(properties: TrustpilotPromptAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('trustpilot.prompt.dismissed', {
      source: 'completion_threshold',
      completion_type: properties.completionType,
      completed_uses: properties.completedUses,
    });
  }

  async captureTrustpilotReviewClick(
    surface: TrustpilotReviewSurface,
    properties?: TrustpilotPromptAnalyticsProperties,
  ): Promise<void> {
    await this.captureBrowserEvent('trustpilot.review.clicked', {
      source: surface,
      completion_type: properties?.completionType,
      completed_uses: properties?.completedUses,
    });
  }

  async captureCookieConsentChoice(properties: CookieConsentAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('cookie.consent_saved', {
      consent_outcome: cookieConsentAnalyticsOutcome(properties),
      audience_measurement: properties.audienceMeasurement,
      communication_tracking: properties.communicationTracking,
    }, true);
  }

  async captureShareOpened(): Promise<void> {
    await this.captureBrowserEvent('ask.share.opened', {
      source: 'public_share_view',
      ...this.getPublicSurfaceProperties(),
    });
  }

  async captureBillingCompanyContactClick(): Promise<void> {
    await this.captureBrowserEvent('billing.company.contact.clicked', {
      source: 'billing_company_onboarding',
    });
  }

  async captureBillingFunnelStep(properties: BillingFunnelAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('billing.funnel_step_reached', {
      funnel_step: properties.step,
      funnel_step_order: BILLING_FUNNEL_STEP_ORDER[properties.step],
      source: properties.source,
      price_key: properties.priceKey,
      funnel_type: properties.funnelType,
      email: properties.email,
    });
  }

  async captureBillingCheckoutCancelled(): Promise<void> {
    await this.captureBrowserEvent('billing.checkout_cancelled', {
      source: 'billing_checkout_return',
    });
  }

  async captureAdrQuizQuestionAnswered(properties: AdrQuizQuestionAnsweredAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('adr_quiz.question_answered', {
      question_id: properties.questionId,
      category: properties.category,
      difficulty: properties.difficulty,
      is_correct: properties.isCorrect,
    });
  }

  async captureAdrQuizCorrectAnswer(properties: AdrQuizAnswerAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('adr_quiz.answer_correct', {
      question_id: properties.questionId,
      category: properties.category,
      difficulty: properties.difficulty,
    });
  }

  async captureAdrQuizIncorrectAnswer(properties: AdrQuizAnswerAnalyticsProperties): Promise<void> {
    await this.captureBrowserEvent('adr_quiz.answer_incorrect', {
      question_id: properties.questionId,
      category: properties.category,
      difficulty: properties.difficulty,
    });
  }

  async captureAdrProfileOnboardingProgress(properties: { step: number; action: 'next' | 'skipped' }): Promise<void> {
    await this.captureBrowserEvent('adr_profile.onboarding_progressed', {
      step: properties.step,
      action: properties.action,
    });
  }

  async captureAdrProfileUpdated(properties: AdrProfileAnalyticsProperties): Promise<void> {
    this.setUserProperties({
      adr_level_code: properties.adrLevelCode,
      adr_role_codes: properties.adrRoleCodes,
      country_code: properties.countryCode,
      explanation_detail_code: properties.explanationDetailCode,
    });
    await this.captureBrowserEvent('adr_profile.updated', {
      source: properties.source,
      outcome: properties.outcome,
      adr_level_code: properties.adrLevelCode,
      adr_role_codes: properties.adrRoleCodes,
      country_code: properties.countryCode,
      explanation_detail_code: properties.explanationDetailCode,
    });
  }

  async captureLoginBackClick(): Promise<void> {
    await this.captureBrowserEvent('auth.back.clicked', {
      source: window.location.pathname === '/register' ? 'register' : 'login',
    });
  }

  async captureAuthSubmitClick(properties: AuthSubmitAnalyticsProperties = {}): Promise<void> {
    const page = properties.page || 'login';
    await this.captureBrowserEvent(`auth.${page}.submit.clicked`, {
      source: page,
      email: properties.email,
      bot_protection_enabled: properties.botProtectionEnabled,
      bot_protection_verified: properties.botProtectionVerified,
    });
  }

  async captureQuotaIncreaseRequest(properties: QuotaIncreaseRequestProperties = {}): Promise<void> {
    await this.captureBrowserEvent('quota.increase_requested', {
      source: 'quota_limit_modal',
      token_reset_at: properties.tokenResetAt,
      token_limit: properties.tokenLimit,
      tokens_spent: properties.tokensSpent,
      remaining_tokens: properties.remainingTokens,
      ...(properties.source ? { source: properties.source } : {}),
    });
  }

  private async captureBrowserEvent(
    event: string,
    properties: Record<string, unknown> = {},
    forceAnonymized = false,
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const mode = forceAnonymized ? 'pseudonymous' : this.collectionMode();
    const isAnonymized = mode === 'pseudonymous';
    this.posthogProvider.capture(
      event,
      sanitizeBrowserAnalyticsProperties({
        ...this.getPageProperties(),
        ...this.getJourneyProperties(properties, isAnonymized),
      }, isAnonymized),
      mode,
    );
  }

  private canUseStandardAnalytics(): boolean {
    return cookieConsentService.readStatus() === null
      || cookieConsentService.canUseAudienceMeasurement();
  }

  private collectionMode(): AnalyticsCollectionMode {
    return this.canUseStandardAnalytics() ? 'standard' : 'pseudonymous';
  }

  private getJourneyProperties(
    properties: Record<string, unknown>,
    isAnonymized: boolean,
  ): Record<string, unknown> {
    if (!isAnonymized) {
      return properties;
    }

    const anonymousProperties = { ...properties };
    delete anonymousProperties.email;
    return anonymousProperties;
  }

  private getPageProperties(includeDisplayProperties = false): Record<string, unknown> {
    const campaignProperties = this.getCampaignProperties();
    return {
      $current_url: window.location.href,
      $pathname: window.location.pathname,
      $host: window.location.host,
      $referrer: document.referrer || undefined,
      $raw_user_agent: navigator.userAgent,
      ...campaignProperties,
      ...(includeDisplayProperties
        ? {
          $title: document.title || undefined,
          $screen_width: window.screen?.width,
          $screen_height: window.screen?.height,
          $viewport_width: window.innerWidth,
          $viewport_height: window.innerHeight,
        }
        : {}),
    };
  }

  private getCampaignProperties(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    return Object.fromEntries(
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
        .map((key) => [key, params.get(key)?.trim()])
        .filter((entry): entry is [string, string] => Boolean(entry[1])),
    );
  }

  private getPublicSurfaceProperties(): Record<string, string> {
    const pathname = window.location.pathname;
    if (/^\/(adr-case|share(?:-withseo)?|question)\//.test(pathname)) {
      return { surface: 'public_share' };
    }
    if (pathname === '/manifesto') {
      return { surface: 'public_manifesto' };
    }
    return {};
  }

}

function cookieConsentAnalyticsOutcome(
  properties: CookieConsentAnalyticsProperties,
): CookieConsentAnalyticsOutcome {
  if (properties.audienceMeasurement && properties.communicationTracking) {
    return 'total_acceptance';
  }

  if (!properties.audienceMeasurement && !properties.communicationTracking) {
    return 'total_refusal';
  }

  return 'partial_acceptance';
}

export const analyticsService = new AnalyticsService();
